// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT - ArtifactDevisInteractif
// Affichage enrichi et interactif des devis dans le panneau Artifacts
// Permet l'édition en temps réel, l'envoi et le téléchargement PDF
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Receipt, Edit3, Save, X, Plus, Trash2, Send, Download,
  User, Building2, Mail, Phone, MapPin, Calendar, Euro,
  Check, Loader2, FileText, Copy, ExternalLink, Percent,
  ChevronDown, ChevronUp, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function ArtifactDevisInteractif({ data, onUpdate, onClose }) {
  const [devis, setDevis] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    client: true,
    lines: true,
    totals: true
  });
  
  // Refs pour les handlers (permettent l'accès dans useEffect)
  const handlersRef = React.useRef({});
  
  // Initialiser avec les données
  useEffect(() => {
    if (data) {
      // Normaliser les données (peut venir de différents formats)
      const normalized = {
        id: data.id || data.devis_id || `devis-${Date.now()}`,
        number: data.number || data.devis_number || data.quote_number || 'BROUILLON',
        status: data.status || 'draft',
        created_at: data.created_at || new Date().toISOString(),
        valid_until: data.valid_until || new Date(Date.now() + 30*24*60*60*1000).toISOString(),
        
        // Client
        client: {
          name: data.client?.name || data.client_name || `${data.client?.first_name || ''} ${data.client?.last_name || ''}`.trim() || 'Client',
          company: data.client?.company || data.client?.company_name || '',
          email: data.client?.email || '',
          phone: data.client?.phone || '',
          address: data.client?.address || '',
          postal_code: data.client?.postal_code || '',
          city: data.client?.city || ''
        },
        
        // Lignes
        lines: data.lines || data.devis?.lines || [
          { id: '1', description: 'Prestation', quantity: 1, unit: 'forfait', unit_price: 0 }
        ],
        
        // Totaux
        vat_rate: data.vat_rate || data.devis?.vat_rate || 10,
        discount_percent: data.discount_percent || 0,
        
        // Métadonnées
        title: data.title || data.object || data.objet || 'Devis',
        description: data.description || data.devis?.description || '',
        notes: data.notes || '',
        portal_link: data.portal_link || null
      };
      
      setDevis(normalized);
    }
  }, [data]);
  
  // Écouter les événements globaux d'actions (DOIT être avant tout return conditionnel)
  useEffect(() => {
    const handleEdit = () => {
      setIsEditing(true);
    };
    
    const handleDownload = (e) => {
      if (e.detail?.type === 'devis') {
        // Appelé depuis handlersRef après le rendu
        handlersRef.current.handleDownloadPDF?.();
      }
    };
    
    const handleSendEmail = (e) => {
      if (e.detail?.type === 'devis') {
        handlersRef.current.handleSend?.();
      }
    };
    
    const handleSendMessage = (e) => {
      if (e.detail?.type === 'devis') {
        toast.info('Envoyer par message - Sélectionne un contact');
      }
    };
    
    window.addEventListener('artifact-edit', handleEdit);
    window.addEventListener('artifact-download', handleDownload);
    window.addEventListener('artifact-send-email', handleSendEmail);
    window.addEventListener('artifact-send-message', handleSendMessage);
    
    return () => {
      window.removeEventListener('artifact-edit', handleEdit);
      window.removeEventListener('artifact-download', handleDownload);
      window.removeEventListener('artifact-send-email', handleSendEmail);
      window.removeEventListener('artifact-send-message', handleSendMessage);
    };
  }, []);
  
  if (!devis) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CALCULS
  // ═══════════════════════════════════════════════════════════════════════════
  const calculateTotals = () => {
    const subtotal = devis.lines.reduce((sum, line) => {
      return sum + (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
    }, 0);
    
    const discountAmount = subtotal * (devis.discount_percent / 100);
    const subtotalAfterDiscount = subtotal - discountAmount;
    const vatAmount = subtotalAfterDiscount * (devis.vat_rate / 100);
    const total = subtotalAfterDiscount + vatAmount;
    
    return { subtotal, discountAmount, subtotalAfterDiscount, vatAmount, total };
  };
  
  const totals = calculateTotals();
  
  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const updateLine = (index, field, value) => {
    const newLines = [...devis.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setDevis({ ...devis, lines: newLines });
  };
  
  const addLine = () => {
    const newLine = {
      id: `line-${Date.now()}`,
      description: '',
      quantity: 1,
      unit: 'unité',
      unit_price: 0
    };
    setDevis({ ...devis, lines: [...devis.lines, newLine] });
  };
  
  const removeLine = (index) => {
    if (devis.lines.length <= 1) {
      toast.error('Le devis doit avoir au moins une ligne');
      return;
    }
    const newLines = devis.lines.filter((_, i) => i !== index);
    setDevis({ ...devis, lines: newLines });
  };
  
  const updateClient = (field, value) => {
    setDevis({
      ...devis,
      client: { ...devis.client, [field]: value }
    });
  };
  
  // Helper pour récupérer l'userId
  const getUserId = () => {
    const ecosystemUser = localStorage.getItem('ecosystem_user');
    const buyerUser = localStorage.getItem('buyer_user');
    const clientUser = localStorage.getItem('client_user');
    
    if (ecosystemUser) {
      try { return JSON.parse(ecosystemUser).id; } catch(e) {}
    }
    if (buyerUser) {
      try { return JSON.parse(buyerUser).id; } catch(e) {}
    }
    if (clientUser) {
      try { return JSON.parse(clientUser).id; } catch(e) {}
    }
    return localStorage.getItem('userId');
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const userId = getUserId();
      if (!userId) {
        toast.error('Tu dois être connecté pour sauvegarder');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/artifacts/devis/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          devis_id: devis.id?.startsWith('devis-') ? devis.id : null,
          user_id: userId,
          title: devis.title,
          description: devis.description,
          client: devis.client,
          lines: devis.lines.map(line => ({
            id: line.id,
            description: line.description,
            quantity: parseFloat(line.quantity) || 0,
            unit: line.unit,
            unit_price: parseFloat(line.unit_price) || 0
          })),
          vat_rate: devis.vat_rate,
          discount_percent: devis.discount_percent,
          notes: devis.notes || ""
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Mettre à jour le devis avec les infos du backend
        setDevis(prev => ({
          ...prev,
          id: data.devis_id,
          number: data.devis_number || prev.number,
          portal_link: data.portal_link ? `${window.location.origin}${data.portal_link}` : prev.portal_link
        }));
        
        toast.success(data.message || 'Devis enregistré');
        setIsEditing(false);
        if (onUpdate) onUpdate({ ...devis, id: data.devis_id });
      } else {
        toast.error(data.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSend = async () => {
    if (!devis.client.email) {
      toast.error('Ajoute l\'email du client pour envoyer le devis');
      return;
    }
    
    // S'assurer que le devis est sauvegardé d'abord
    if (!devis.id?.startsWith('devis-')) {
      toast.info('Sauvegarde du devis en cours...');
      await handleSave();
    }
    
    setIsSending(true);
    try {
      const userId = getUserId();
      
      const response = await fetch(`${API_URL}/api/artifacts/devis/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          devis_id: devis.id,
          user_id: userId,
          send_email: true,
          email_message: ""
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setDevis(prev => ({ ...prev, status: 'sent' }));
        if (data.email_sent) {
          toast.success(`Devis envoyé à ${devis.client.email}`);
        } else {
          toast.success('Devis marqué comme envoyé');
        }
        
        // Mettre à jour le portal_link si disponible
        if (data.portal_link) {
          setDevis(prev => ({
            ...prev,
            portal_link: `${window.location.origin}${data.portal_link}`
          }));
        }
      } else {
        toast.error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setIsSending(false);
    }
  };
  
  const handleDownloadPDF = async () => {
    if (!devis.id?.startsWith('devis-')) {
      toast.error('Enregistre d\'abord le devis pour télécharger le PDF');
      return;
    }
    
    try {
      toast.loading('Génération du PDF...', { id: 'pdf-loading' });
      
      const response = await fetch(`${API_URL}/api/artifacts/devis/${devis.id}/pdf`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }
      
      // Récupérer le blob
      const blob = await response.blob();
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis_${devis.number || devis.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé', { id: 'pdf-loading' });
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Erreur lors de la génération du PDF', { id: 'pdf-loading' });
    }
  };
  
  const copyPortalLink = () => {
    if (devis.portal_link) {
      navigator.clipboard.writeText(devis.portal_link);
      toast.success('Lien copié');
    }
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Mettre à jour les refs des handlers pour les événements globaux
  handlersRef.current = {
    handleSend,
    handleDownloadPDF
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  
  const statusColors = {
    draft: 'bg-gray-100 text-gray-600',
    sent: 'bg-blue-100 text-blue-700',
    viewed: 'bg-amber-100 text-amber-700',
    signed: 'bg-emerald-100 text-emerald-700',
    refused: 'bg-red-100 text-red-700'
  };
  
  const statusLabels = {
    draft: 'Brouillon',
    sent: 'Envoyé',
    viewed: 'Consulté',
    signed: 'Signé',
    refused: 'Refusé'
  };
  
  return (
    <div className="h-full flex flex-col bg-white rounded-xl overflow-hidden">
      {/* ══════════════════════════════════════════════════════════════════════
          HEADER SIMPLE (sans boutons)
      ══════════════════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6] px-4 py-3 text-white">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            <span className="font-semibold">{devis.number}</span>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[devis.status]}`}>
            {statusLabels[devis.status]}
          </span>
        </div>
        <h3 className="text-lg font-bold truncate">{devis.title}</h3>
      </div>
      
      {/* ══════════════════════════════════════════════════════════════════════
          BARRE D'ÉDITION (visible uniquement en mode édition)
      ══════════════════════════════════════════════════════════════════════ */}
      {isEditing && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
          <span className="text-xs text-amber-700 font-medium">Mode édition</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsEditing(false)}
              className="px-3 py-1.5 rounded-lg hover:bg-amber-100 transition-colors text-amber-700 text-sm"
              title="Annuler"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 transition-colors text-white text-sm disabled:opacity-50 flex items-center gap-1"
              title="Enregistrer"
            >
              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Enregistrer
            </button>
          </div>
        </div>
      )}
      
      {/* ══════════════════════════════════════════════════════════════════════
          CONTENU SCROLLABLE
      ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        
        {/* Section Client */}
        <section className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('client')}
            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="font-medium text-gray-700">Client</span>
            </div>
            {expandedSections.client ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          
          <AnimatePresence>
            {expandedSections.client && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-3 space-y-2">
                  {isEditing ? (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={devis.client.name}
                          onChange={(e) => updateClient('name', e.target.value)}
                          placeholder="Nom complet"
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <input
                          type="text"
                          value={devis.client.company}
                          onChange={(e) => updateClient('company', e.target.value)}
                          placeholder="Entreprise (optionnel)"
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="email"
                          value={devis.client.email}
                          onChange={(e) => updateClient('email', e.target.value)}
                          placeholder="Email"
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <input
                          type="tel"
                          value={devis.client.phone}
                          onChange={(e) => updateClient('phone', e.target.value)}
                          placeholder="Téléphone"
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                      </div>
                      <input
                        type="text"
                        value={devis.client.address}
                        onChange={(e) => updateClient('address', e.target.value)}
                        placeholder="Adresse"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={devis.client.postal_code}
                          onChange={(e) => updateClient('postal_code', e.target.value)}
                          placeholder="CP"
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <input
                          type="text"
                          value={devis.client.city}
                          onChange={(e) => updateClient('city', e.target.value)}
                          placeholder="Ville"
                          className="col-span-2 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-gray-800">{devis.client.name}</p>
                      {devis.client.company && <p className="text-gray-600">{devis.client.company}</p>}
                      {devis.client.email && (
                        <p className="flex items-center gap-1 text-gray-500">
                          <Mail className="w-3 h-3" /> {devis.client.email}
                        </p>
                      )}
                      {devis.client.phone && (
                        <p className="flex items-center gap-1 text-gray-500">
                          <Phone className="w-3 h-3" /> {devis.client.phone}
                        </p>
                      )}
                      {(devis.client.address || devis.client.city) && (
                        <p className="flex items-center gap-1 text-gray-500">
                          <MapPin className="w-3 h-3" /> 
                          {[devis.client.address, devis.client.postal_code, devis.client.city].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
        
        {/* Section Lignes */}
        <section className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('lines')}
            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="font-medium text-gray-700">Prestations ({devis.lines.length})</span>
            </div>
            {expandedSections.lines ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          
          <AnimatePresence>
            {expandedSections.lines && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-3 space-y-2">
                  {devis.lines.map((line, index) => (
                    <div key={line.id || index} className="bg-gray-50 rounded-lg p-3">
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <input
                              type="text"
                              value={line.description}
                              onChange={(e) => updateLine(index, 'description', e.target.value)}
                              placeholder="Description"
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                            <button
                              onClick={() => removeLine(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <input
                              type="number"
                              value={line.quantity}
                              onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                              placeholder="Qté"
                              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                            <select
                              value={line.unit}
                              onChange={(e) => updateLine(index, 'unit', e.target.value)}
                              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            >
                              <option value="unité">Unité</option>
                              <option value="m²">m²</option>
                              <option value="ml">ml</option>
                              <option value="h">Heure</option>
                              <option value="jour">Jour</option>
                              <option value="forfait">Forfait</option>
                            </select>
                            <input
                              type="number"
                              value={line.unit_price}
                              onChange={(e) => updateLine(index, 'unit_price', e.target.value)}
                              placeholder="Prix unit."
                              step="0.01"
                              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                            <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-right font-medium">
                              {((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0)).toFixed(2)} €
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{line.description || 'Prestation'}</p>
                            <p className="text-xs text-gray-500">
                              {line.quantity} {line.unit} × {parseFloat(line.unit_price).toFixed(2)} €
                            </p>
                          </div>
                          <p className="font-semibold text-gray-800">
                            {((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0)).toFixed(2)} €
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {isEditing && (
                    <button
                      onClick={addLine}
                      className="w-full flex items-center justify-center gap-2 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter une ligne
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
        
        {/* Section Totaux */}
        <section className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('totals')}
            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Euro className="w-4 h-4 text-gray-500" />
              <span className="font-medium text-gray-700">Totaux</span>
            </div>
            {expandedSections.totals ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          
          <AnimatePresence>
            {expandedSections.totals && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Sous-total HT</span>
                    <span className="font-medium">{totals.subtotal.toFixed(2)} €</span>
                  </div>
                  
                  {isEditing && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Percent className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 text-sm">Remise</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={devis.discount_percent}
                          onChange={(e) => setDevis({ ...devis, discount_percent: parseFloat(e.target.value) || 0 })}
                          className="w-16 px-2 py-1 border border-gray-200 rounded text-sm text-right"
                          min="0"
                          max="100"
                        />
                        <span className="text-gray-500">%</span>
                      </div>
                    </div>
                  )}
                  
                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Remise ({devis.discount_percent}%)</span>
                      <span>-{totals.discountAmount.toFixed(2)} €</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">TVA</span>
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <select
                          value={devis.vat_rate}
                          onChange={(e) => setDevis({ ...devis, vat_rate: parseFloat(e.target.value) })}
                          className="px-2 py-1 border border-gray-200 rounded text-sm"
                        >
                          <option value={0}>0%</option>
                          <option value={5.5}>5.5%</option>
                          <option value={10}>10%</option>
                          <option value={20}>20%</option>
                        </select>
                      </div>
                    ) : (
                      <span className="font-medium text-sm">{devis.vat_rate}% = {totals.vatAmount.toFixed(2)} €</span>
                    )}
                  </div>
                  
                  <div className="pt-2 border-t border-gray-200 flex justify-between">
                    <span className="font-semibold text-gray-800">Total TTC</span>
                    <span className="text-xl font-bold text-[#1E3A5F]">{totals.total.toFixed(2)} €</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
        
        {/* Lien portail */}
        {devis.portal_link && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-xs text-blue-600 font-medium mb-2">Lien de signature client</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={devis.portal_link}
                readOnly
                className="flex-1 px-3 py-2 bg-white border border-blue-200 rounded-lg text-sm text-gray-600"
              />
              <button
                onClick={copyPortalLink}
                className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors text-blue-600"
              >
                <Copy className="w-4 h-4" />
              </button>
              <a
                href={devis.portal_link}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors text-blue-600"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
